/**
 * Player-side touch tweaks applied on top of the walk's own viewer.
 *
 * The viewer runs in a same-origin iframe (served by our service worker), so
 * the Player can listen on its window in the capture phase - before any of
 * the viewer's listeners - and rewrite touch input it was not built for:
 *
 * - Invert look: drags that start on a large element (the canvas / look
 *   zone) are re-dispatched with mirrored movement. Small elements (an
 *   on-screen joystick, buttons) are left alone.
 * - Pinch to change focal length: two fingers on the look surface change
 *   the camera's focal length (12-120 mm) through the viewer's
 *   `window.walkViewer` hook. Viewers without the hook get the optical
 *   equivalent of a longer lens instead: the frame is scaled (CSS transform,
 *   1x-4x) around the fingers. The viewer never sees the two-finger gesture.
 *
 * Look sensitivity follows the lens in the viewer (it reads camera.fov per drag, so the pinch above
 * is covered). In the no-hook fallback the frame's CSS scale already makes a screen pixel worth
 * 1/scale frame pixels, i.e. the same proportional rule.
 *
 * Invert look relies on the viewer using pointer events to look; see
 * docs/WALK_FORMAT.md ("Touch input the Player adjusts").
 */

export interface InputSettings {
  invertLookX: boolean;
  invertLookY: boolean;
  pinchFocalLength: boolean;
}

/**
 * Optional hook a viewer exposes as `window.walkViewer` (see
 * docs/WALK_FORMAT.md). Focal length in mm on a 35 mm film gauge, as
 * three.js PerspectiveCamera.getFocalLength/setFocalLength.
 */
interface WalkViewerHook {
  getFocalLength(): number;
  setFocalLength(mm: number): void;
}

export const MIN_FOCAL_LENGTH_MM = 12;
export const MAX_FOCAL_LENGTH_MM = 120;

function viewerHook(win: Window): WalkViewerHook | null {
  const hook = (win as unknown as { walkViewer?: Partial<WalkViewerHook> }).walkViewer;
  return typeof hook?.getFocalLength === "function" && typeof hook.setFocalLength === "function"
    ? (hook as WalkViewerHook)
    : null;
}

/**
 * Fixed, not settings (issue walk-bake#11, C14): look inverted left / right, not up / down, and
 * a two-finger pinch changes the focal length. Old saved choices are never read.
 */
export const INPUT_SETTINGS: InputSettings = {
  invertLookX: true,
  invertLookY: false,
  pinchFocalLength: true,
};

const MAX_ZOOM = 4;
/** A pointerdown target covering at least this share of the viewport is a look surface. */
const LOOK_SURFACE_MIN_AREA = 0.2;

interface TrackedPointer {
  look: boolean;
  pinch: boolean;
  lastX: number;
  lastY: number;
  /** Position in the frame's (unzoomed) layout box, i.e. what the user sees. */
  screenX: number;
  screenY: number;
  /** Position the viewer sees (mirrored when inverting). */
  virtualX: number;
  virtualY: number;
  /** Last event forwarded to the viewer, to cancel it if a pinch starts. */
  last: PointerEvent;
}

interface Pinch {
  a: number;
  b: number;
  startDistance: number;
  startScale: number;
  /** Set when the viewer exposes the focal-length hook. */
  startFocalLength: number | null;
  /** Viewer point (frame-local CSS px) that stays under the fingers' midpoint. */
  anchorX: number;
  anchorY: number;
}

/** Installs the tweaks on a viewer frame; returns an uninstall function. */
export function installInputTweaks(
  frame: HTMLIFrameElement,
  /** Called while pinching with the focal length (mm) or, without the hook, the zoom factor. */
  onPinch?: (value: { focalLengthMm: number } | { zoom: number }) => void,
): () => void {
  const win = frame.contentWindow;
  if (!win) throw new Error("Viewer frame has no window");
  const PointerEventCtor = (win as unknown as { PointerEvent: typeof PointerEvent }).PointerEvent;
  const synthetic = new WeakSet<Event>();
  const pointers = new Map<number, TrackedPointer>();
  let pinch: Pinch | null = null;
  // Frame transform: translate(tx, ty) scale(scale), origin top-left.
  let scale = 1;
  let tx = 0;
  let ty = 0;

  const applyZoom = (nextScale: number, nextTx: number, nextTy: number): void => {
    scale = Math.min(MAX_ZOOM, Math.max(1, nextScale));
    // Keep the zoomed frame covering its box: no empty edges.
    tx = Math.min(0, Math.max(frame.clientWidth * (1 - scale), nextTx));
    ty = Math.min(0, Math.max(frame.clientHeight * (1 - scale), nextTy));
    frame.style.transformOrigin = "0 0";
    frame.style.transform = scale === 1 ? "" : `translate(${tx}px, ${ty}px) scale(${scale})`;
  };

  /** Frame-local event coordinates -> position in the frame's layout box. */
  const toScreen = (e: PointerEvent): [number, number] => [tx + scale * e.clientX, ty + scale * e.clientY];

  const swallow = (e: Event): void => {
    e.stopImmediatePropagation();
    if (e.cancelable) e.preventDefault();
  };

  const isLookSurface = (target: EventTarget | null): boolean => {
    // Duck-typed: the target belongs to the iframe's realm, not ours.
    const element = target as Element | null;
    if (typeof element?.getBoundingClientRect !== "function") return false;
    const rect = element.getBoundingClientRect();
    return rect.width * rect.height >= LOOK_SURFACE_MIN_AREA * win.innerWidth * win.innerHeight;
  };

  const forward = (e: PointerEvent, type: string, x: number, y: number, movementX: number, movementY: number): void => {
    const event = new PointerEventCtor(type, {
      bubbles: e.bubbles,
      cancelable: e.cancelable,
      composed: true,
      view: win,
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      isPrimary: e.isPrimary,
      width: e.width,
      height: e.height,
      pressure: e.pressure,
      button: e.button,
      buttons: e.buttons,
      clientX: x,
      clientY: y,
      screenX: e.screenX + (x - e.clientX),
      screenY: e.screenY + (y - e.clientY),
      movementX,
      movementY,
    });
    synthetic.add(event);
    (e.target ?? win).dispatchEvent(event);
  };

  /** Distance and midpoint of the two pinch fingers, in the frame's layout box. */
  const pinchGeometry = (): { distance: number; midX: number; midY: number } | null => {
    const a = pinch && pointers.get(pinch.a);
    const b = pinch && pointers.get(pinch.b);
    if (!a || !b) return null;
    return {
      distance: Math.hypot(a.screenX - b.screenX, a.screenY - b.screenY),
      midX: (a.screenX + b.screenX) / 2,
      midY: (a.screenY + b.screenY) / 2,
    };
  };

  const onDown = (e: PointerEvent): void => {
    if (synthetic.has(e) || e.pointerType === "mouse") return;
    const settings = INPUT_SETTINGS;
    if (pinch) return swallow(e); // a third finger: ignore
    const [screenX, screenY] = toScreen(e);
    const tracked: TrackedPointer = {
      look: isLookSurface(e.target),
      pinch: false,
      lastX: e.clientX,
      lastY: e.clientY,
      screenX,
      screenY,
      virtualX: e.clientX,
      virtualY: e.clientY,
      last: e,
    };
    // Joystick + look with two thumbs stays untouched: only two fingers on
    // the look surface make a pinch.
    const [firstId, first] = [...pointers.entries()].find(([, p]) => p.look) ?? [];
    if (settings.pinchFocalLength && tracked.look && first && firstId !== undefined) {
      // Second finger: take over both as a pinch. Cancel the first one in the viewer.
      forward(first.last, "pointercancel", first.virtualX, first.virtualY, 0, 0);
      first.pinch = tracked.pinch = true;
      pointers.set(e.pointerId, tracked);
      const hook = viewerHook(win);
      pinch = {
        a: firstId,
        b: e.pointerId,
        startDistance: 0,
        startScale: scale,
        startFocalLength: hook ? hook.getFocalLength() : null,
        anchorX: 0,
        anchorY: 0,
      };
      const start = pinchGeometry();
      if (start) {
        pinch.startDistance = start.distance;
        pinch.anchorX = (start.midX - tx) / scale;
        pinch.anchorY = (start.midY - ty) / scale;
      }
      return swallow(e);
    }
    pointers.set(e.pointerId, tracked);
  };

  const onMove = (e: PointerEvent): void => {
    if (synthetic.has(e)) return;
    const tracked = pointers.get(e.pointerId);
    if (!tracked) return;
    const dx = e.clientX - tracked.lastX;
    const dy = e.clientY - tracked.lastY;
    tracked.lastX = e.clientX;
    tracked.lastY = e.clientY;
    [tracked.screenX, tracked.screenY] = toScreen(e);
    if (tracked.pinch) {
      swallow(e);
      const now = pinchGeometry();
      if (!pinch || !now || pinch.startDistance === 0) return;
      // Zoom by the change in finger spread, keeping the anchor under the midpoint.
      const spread = now.distance / pinch.startDistance;
      const hook = viewerHook(win);
      if (hook && pinch.startFocalLength) {
        // Spreading = longer lens (closer), pinching = wider angle.
        const mm = Math.min(MAX_FOCAL_LENGTH_MM, Math.max(MIN_FOCAL_LENGTH_MM, pinch.startFocalLength * spread));
        hook.setFocalLength(mm);
        onPinch?.({ focalLengthMm: mm });
        return;
      }
      // No hook: scaling the image from the same viewpoint is what a longer
      // lens does, so it stands in for the tele side.
      const nextScale = Math.min(MAX_ZOOM, Math.max(1, pinch.startScale * spread));
      applyZoom(nextScale, now.midX - nextScale * pinch.anchorX, now.midY - nextScale * pinch.anchorY);
      onPinch?.({ zoom: scale });
      return;
    }
    const { invertLookX, invertLookY } = INPUT_SETTINGS;
    if (!tracked.look || (!invertLookX && !invertLookY)) {
      tracked.virtualX += dx;
      tracked.virtualY += dy;
      tracked.last = e;
      return;
    }
    swallow(e);
    tracked.virtualX += invertLookX ? -dx : dx;
    tracked.virtualY += invertLookY ? -dy : dy;
    tracked.last = e;
    forward(
      e,
      e.type,
      tracked.virtualX,
      tracked.virtualY,
      invertLookX ? -e.movementX : e.movementX,
      invertLookY ? -e.movementY : e.movementY,
    );
  };

  const onUp = (e: PointerEvent): void => {
    if (synthetic.has(e)) return;
    const tracked = pointers.get(e.pointerId);
    if (!tracked) return;
    pointers.delete(e.pointerId);
    if (tracked.pinch) {
      swallow(e);
      // Lifting either finger ends the pinch (zoom stays); the other stays ignored until lifted.
      pinch = null;
      return;
    }
    const { invertLookX, invertLookY } = INPUT_SETTINGS;
    if (tracked.look && (invertLookX || invertLookY)) {
      swallow(e);
      forward(e, e.type, tracked.virtualX, tracked.virtualY, 0, 0);
    }
  };

  // Viewers built on touch events: at least keep the two-finger gesture from
  // also turning the camera while pinch-to-zoom is on.
  const onTouch = (e: TouchEvent): void => {
    if (INPUT_SETTINGS.pinchFocalLength && e.touches.length >= 2) e.stopImmediatePropagation();
  };

  const listeners: [string, EventListener][] = [
    ["pointerdown", onDown as EventListener],
    ["pointermove", onMove as EventListener],
    ["pointerup", onUp as EventListener],
    ["pointercancel", onUp as EventListener],
    ["touchstart", onTouch as EventListener],
    ["touchmove", onTouch as EventListener],
  ];
  for (const [type, listener] of listeners) {
    win.addEventListener(type, listener, { capture: true, passive: false });
  }
  return () => {
    applyZoom(1, 0, 0);
    for (const [type, listener] of listeners) win.removeEventListener(type, listener, { capture: true });
  };
}
